import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { CollectionArrow } from '@sorare/core/src/atoms/icons/CollectionArrow';
import { NewScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { isType } from '@sorare/core/src/lib/gql';

import { Captain } from 'components/so5/Captain';

import type { OnboardingComposeRules_so5Leaderboard } from './__generated__/index.graphql';
import captainRuleBg from './captain-rule-bg.png';
import scarcityRuleBg from './scarcity-rule-bg.png';

const BoostChip = styled(Horizontal).attrs({ center: true, gap: 0.5 })`
  position: absolute;
  top: var(--intermediate-unit);
  right: calc(-1 * var(--unit));
  padding: var(--half-unit);
  border-radius: var(--unit) var(--half-unit) 0px var(--unit);
  background: var(--c-nd-100-opaque);
`;
const RuleWrapper = styled(Vertical)`
  flex: 1;
  align-items: center;
`;
const RuleBlockWrapper = styled(Vertical).attrs({ center: true })`
  border: 1px solid var(--c-nd-50);
  padding: var(--triple-unit) 0 var(--double-unit);
  align-self: stretch;
  border-radius: var(--double-unit);
  position: relative;
`;

const ScarcityRuleBlockWrapper = styled(RuleBlockWrapper)`
  background: linear-gradient(
    180deg,
    var(--c-black) 59.65%,
    var(--c-nd-100-opaque) 100%
  );
`;

const CaptainRuleBlockWrapper = styled(RuleBlockWrapper)`
  background: linear-gradient(180deg, var(--c-black) 59.65%, #30001a 100%);
`;

type Props = {
  so5Leaderboard: OnboardingComposeRules_so5Leaderboard;
};

export const OnboardingComposeRules = ({ so5Leaderboard }: Props) => {
  const { displayedTypedRules = [] } = so5Leaderboard;

  return (
    <Horizontal gap={2}>
      {displayedTypedRules.map(rule => {
        if (isType(rule, 'So5ScarcityRule')) {
          return (
            <RuleWrapper key={rule.key} center gap={2}>
              <ScarcityRuleBlockWrapper>
                <img
                  src={scarcityRuleBg}
                  alt=""
                  style={{
                    width: '100%',
                  }}
                />
                <LabelS color="var(--c-nd-600)">
                  {so5Leaderboard.rules?.appearances
                    ?.map(a => a.shortName)
                    .join(', ')}
                </LabelS>
              </ScarcityRuleBlockWrapper>
              <Horizontal gap={0.5}>
                <NewScarcityIcon rarity={Rarity.common} size={2} />
                <LabelM bold>
                  <FormattedMessage
                    id="OnboardingComposeRules.5Cards"
                    defaultMessage="5 Cards"
                  />
                </LabelM>
              </Horizontal>
            </RuleWrapper>
          );
        }
        if (isType(rule, 'So5CaptainScarcityRule')) {
          return (
            <RuleWrapper key={rule.key} center gap={2}>
              <CaptainRuleBlockWrapper>
                <img
                  src={captainRuleBg}
                  alt=""
                  style={{
                    width: '72%',
                  }}
                />
                <LabelS color="var(--c-nd-600)">
                  <FormattedMessage
                    id="OnboardingComposeRules.anyPosition"
                    defaultMessage="Any position"
                  />
                </LabelS>
                <BoostChip>
                  <CollectionArrow color="var(--c-score-high)" />
                  <LabelS>
                    <FormattedMessage
                      id="OnboardingComposeRules.boost"
                      defaultMessage="{bonus, number, percent} Boost"
                      values={{ bonus: rule.bonus }}
                    />
                  </LabelS>
                </BoostChip>
              </CaptainRuleBlockWrapper>
              <Horizontal gap={0.5}>
                <Captain active />
                <LabelM bold>
                  <FormattedMessage
                    id="OnboardingComposeRules.1Captain"
                    defaultMessage="1 Captain"
                  />
                </LabelM>
              </Horizontal>
            </RuleWrapper>
          );
        }
        return null;
      })}
    </Horizontal>
  );
};

OnboardingComposeRules.fragments = {
  so5Leaderboard: gql`
    fragment OnboardingComposeRules_so5Leaderboard on So5Leaderboard {
      slug
      rules {
        id
        appearances {
          name
          shortName
        }
      }

      displayedTypedRules {
        key
        ... on So5CaptainScarcityRule {
          key
          bonus
        }
      }
    }
  ` as TypedDocumentNode<OnboardingComposeRules_so5Leaderboard>,
};
